import {
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { Form, message } from 'antd';

import { HeaderTabCommerce } from '../../components/TabsPropertyDataCommerce/components';
import { TabsPropertyDataCommerce } from '../../components/TabsPropertyDataCommerce/index';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared/contexts';
import { StepThreeFormContext } from './StepThreeFormContext';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';

import {
  Feature,
  FeaturesVariousRisksRequest,
  RequestPlansByProducer,
  SelectOption,
  GetPlansByProducerAndActivityResponse,
  DECISION_TYPE,
} from '../../../../../../../../../shared/interfaces';

import { LOADING_SAVED_QUOTATIONS_STATES, PLAN_TYPE, QuotationStep } from '../../../../../shared';
import { displayErrorMessage } from '../../../../../../../../../shared/utils';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {  
  const [stepThreeForm] = Form.useForm<any>();

  const { clientSelected } = useContext(StepOneFormContext);
  const {
    currentStep,
    quotationInfo,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    savedQuotation,
    quotationToRenewal,
    setRenewalQuotationState,
    renewalQuotationState
  } = useContext(QuotationInfoContext);
  const { 
    setCanShowSpinner, 
    getLocalitiesPerProvince, 
    updateFormsAfterRemoveTabs,
    removedTabIndexes,
    setRemovedTabIndexes,
    setUserCanDeleteTabs
  } = useContext(GenericDataFormContext);

  const { rubricSelected } = useContext(StepTwoFormContext);

  const INITIAL_KEY_TAB = savedQuotation ? savedQuotation.bienes.length : 1;

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const [commerceFormValues, setCommerceFormValues] =
      useState<any>(null);
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [allFeatures, setAllFeatures] = useState<
    Array<{ planCode: string; keyTab: number; features: Array<Feature> }>
  >([]);
  const [hasClose, setHasClose] = useState<boolean>(false);
  const [hasOpen, setHasOpen] = useState<boolean>(false);
  const [keyTabToDelete, setKeyTabToDelete] = useState<number | null>(null);
  const [plansByActivityList, setPlansByActivityList] = useState<
    Array<SelectOption>
  >([]);
  const [plansByActivityListCopy, setPlansByActivityListCopy] = useState<any>();
  const [showModal, setShowModal] = useState<boolean>(false);

  const [tabs, setTabs] = useState<Array<any>>([
    {
      label: <HeaderTabCommerce id={INITIAL_KEY_TAB} />,
      children: <TabsPropertyDataCommerce id={INITIAL_KEY_TAB} />,
      key: INITIAL_KEY_TAB,
      closable: false,
      forceRender: true,
    },
  ]);

  const resetTabs = useCallback(() => {
    setTabs([
      {
        label: <HeaderTabCommerce id={INITIAL_KEY_TAB} />,
        children: <TabsPropertyDataCommerce id={INITIAL_KEY_TAB} />,
        key: INITIAL_KEY_TAB,
        closable: false,
        forceRender: true,
      },
    ]);

    stepThreeForm.resetFields();
    newTabIndex.current = INITIAL_KEY_TAB;
    setActiveKey(INITIAL_KEY_TAB);
  }, [
    tabs, 
    setTabs, 
    setActiveKey,
    stepThreeForm,
    newTabIndex,
  ]);

  const addNewTab = useCallback(() => {
    if (tabs.length <= 5) {
      const newActiveKey = ++newTabIndex.current;

      setTabs([
        ...tabs,
        {
          label: <HeaderTabCommerce id={newActiveKey} />,
          children: <TabsPropertyDataCommerce id={newActiveKey} />,
          key: newActiveKey,
          closable: false,
          forceRender: true,
        },
      ]);

      setActiveKey(newActiveKey);

      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };

      return;
    }

    displayErrorMessage('Solo puede agregar hasta 6 Comercios');
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const duplicateTab = useCallback(
    (id: any) => {
      if (tabs.length <= 5) {
        const tabSelected = tabs.find((tab: any) => tab.key === id);
        const newActiveKey = ++newTabIndex.current;

        if (tabSelected) {
          const newTab = {
            ...tabSelected,
            key: newActiveKey,
            label: <HeaderTabCommerce id={newActiveKey} />,
            children: (
              <TabsPropertyDataCommerce id={newActiveKey} duplicateId={id} />
            ),
            closable: false,
            forceRender: true,
          };

          setTabs([...tabs, newTab]);
          setActiveKey(newActiveKey);
          if(removedTabIndexes.includes(newActiveKey)) {
            setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
          };
        }

        return;
      }

      displayErrorMessage('Solo puede agregar hasta 6 Comercios');
    },
    [
      tabs, 
      newTabIndex, 
      setTabs, 
      setActiveKey,
      removedTabIndexes,
      setRemovedTabIndexes,
    ]
  );

  const removeTab = useCallback(
    (targetKey: number) => {
      setCanShowSpinner(true);

      setTimeout(() => {
        updateFormsAfterRemoveTabs([targetKey], [stepThreeForm]);

        setRemovedTabIndexes((prevValues) => [ ...prevValues, targetKey ]);

        const targetIndex = tabs.findIndex(
          (pane: any) => pane.key === targetKey
        );

        const newPanes = tabs.filter((pane: any) => pane.key !== targetKey).map((el, i) => ({
          ...el,
          key: i + 1,
          label: <HeaderTabCommerce id={i + 1} />,
          children: <TabsPropertyDataCommerce id={i + 1} />,
        }));

        if (newPanes.length && targetKey === activeKey) {
          const { key } =
            newPanes[
              targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
            ];
          setActiveKey(key);
        }

        newTabIndex.current = tabs.length - 1; 

        setTabs(newPanes);
        setCanShowSpinner(false);
      }, 500);
    },
    [
      tabs, 
      setTabs, 
      setActiveKey, 
      activeKey,
      updateFormsAfterRemoveTabs,
      stepThreeForm,
      setRemovedTabIndexes
    ]
  );

  const checkPlanType = (response: GetPlansByProducerAndActivityResponse) => {
    const hasType = (type: string) =>
      response.planes.some((plan) => plan.tipoDePlan.toUpperCase() === type);

    setHasOpen(hasType(PLAN_TYPE.OPEN));
    setHasClose(hasType(PLAN_TYPE.CLOSED));
  };

  const getListPlansActivity = useCallback(async () => {
    setCanShowSpinner(true);

    const params: RequestPlansByProducer = {
      capituloTarifaInciso1: rubricSelected?.capituloTarifaInciso1!,
      arse: quotationInfo?.arse!,
      articulo: quotationInfo?.article!,
      capituloTarifaInciso2: rubricSelected?.capituloTarifaInciso2!,
      capituloTarifa: rubricSelected?.capituloTarifa!,
      rama: quotationInfo?.branch!,
    };

    try {
      const response = await QuotationService.getPlansByProducerAndActivity(
        params
      );
      checkPlanType(response.data);
      setPlansByActivityListCopy(response);

      if (response?.data.planes.length) {
        const options = response.data.planes.map((el) => ({
          label: el.descripcionLarga,
          value: el.codigo,
        }));

        setPlansByActivityList(options);
      }
    } catch {
    } finally {
      setCanShowSpinner(false);
    }
  }, [rubricSelected, quotationInfo]);

  const getFeaturesRisks = useCallback(
    async (
      planSelected: string,
      componente: number
    ): Promise<Array<Feature> | any> => {
      const params: FeaturesVariousRisksRequest = {
        numeroCotizacion: quotationInfo?.token!,
        rama: quotationInfo?.branch!,
        articuloRamaSecuencia: quotationInfo?.arse!,
        plan: planSelected,
        codigoPostal: clientSelected?.codigoPostal!,
        sufijoPostal: clientSelected?.sufijoCodigoPostal!,
        componente,
        ubicacionDeRiesgo: '',
      };

      try {
        const response = await QuotationService.postFeaturesVariousRisks(
          params
        );

        return response?.data?.caracteristicas ?? [];
      } catch {}
    },
    [quotationInfo, clientSelected]
  );

  const getFeaturesRiskByPlan = useCallback(
    async (id: number, planSelected: string, componente: number) => {
      const isFeatureAlreadyAdded = allFeatures.some(
        (feature) => feature.planCode === planSelected
      );

      if (isFeatureAlreadyAdded) return;

      setCanShowSpinner(true);

      try {
        const featuresList = await getFeaturesRisks(planSelected, componente);

        setAllFeatures((prevValues) => [
          ...prevValues,
          { planCode: planSelected, keyTab: id, features: featuresList },
        ]);
      } catch {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      setCanShowSpinner, 
      setAllFeatures, 
      getFeaturesRisks
    ]
  );

  const fillStepThreeServices = useCallback(async (isRenewal:boolean) => {
    await getListPlansActivity();
    let quotation;
    if (isRenewal) {
      quotation = quotationToRenewal;
    }else{
      quotation = savedQuotation;
    }    
    quotation.bienes.map((bien: any) =>
      getFeaturesRiskByPlan(
        bien.numeroComponente,
        bien.planSeleccionado.toString(),
        Number(bien.numeroComponente)
      )
    );
    quotation.bienes.forEach(async (bien:any) => {
      await getLocalitiesPerProvince(bien.provincia)
    })
  }, [
    savedQuotation, 
    quotationToRenewal,
    getListPlansActivity,
    getFeaturesRiskByPlan,
    getLocalitiesPerProvince,
  ]);

  const fillStepThreeData = useCallback(async (isRenewal:boolean) => {
    setUserCanDeleteTabs(false);
    let quotation;
    if (isRenewal) {
      quotation = quotationToRenewal;
    }else{
      quotation = savedQuotation;
    }
    const _tabs: any = [];
    const _inputs: any = [];
    quotation.bienes.forEach((good: any, index: number) => {
      const _activeKey = index + 1;

      _tabs.push({
        label: <HeaderTabCommerce id={_activeKey} />,
        children: <TabsPropertyDataCommerce id={_activeKey} />,
        key: _activeKey,
        closable: index !== 0,
        forceRender: true,
      });

      _inputs.push({ 
        name: `plan-tab-${_activeKey}`, 
        value: `${good.planSeleccionado}` 
      });
      _inputs.push({ 
        name: `province-tab-${_activeKey}`, 
        value: `${good.provincia}` 
      });
      _inputs.push({ 
        name: `location-tab-${_activeKey}`, 
        value: `${good.sufijoPostal}${good.codigoPostal}` 
      });
      _inputs.push({
        name: `propertyDataAddress${_activeKey}`,
        value: `${good.domicilio}`,
      });

      allFeatures.find((feature) => feature.keyTab === _activeKey)?.features.forEach((_feature) => {
        const apply = good.caracteristicasSeleccionadas.find((characteristic: any) => characteristic.codigo === _feature.codigo && characteristic.tiene === DECISION_TYPE.YES) !== undefined;
        _inputs.push({ 
          name: `feature-tab-${_activeKey}-${_feature.codigo}-${_feature.codigoEquivalente}`, 
          value: apply
        });
      });
    });

    setTabs([
      ..._tabs
    ]);
    setActiveKey(INITIAL_KEY_TAB);
    stepThreeForm.setFields(_inputs);
    setCommerceFormValues(_inputs);

  }, [
    savedQuotation,
    setActiveKey,
    setTabs,
    stepThreeForm,
    allFeatures,
    setCommerceFormValues
  ]);

  useEffect(() => {
    if (
      rubricSelected &&
      currentStep === QuotationStep.THREE &&
      !plansByActivityList.length
    ) {
      getListPlansActivity();
    }
  }, [rubricSelected, getListPlansActivity, currentStep, plansByActivityList]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING);
      fillStepThreeServices(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      plansByActivityList &&
      allFeatures &&
      allFeatures.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    plansByActivityList,
    allFeatures,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING);
      fillStepThreeData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    tabs,
  ]);

  /*
  * Arranca renovacion
  */

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL);
      fillStepThreeServices(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
      plansByActivityList &&
      allFeatures &&
      allFeatures.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationToRenewal,
    plansByActivityList,
    allFeatures,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL);
      fillStepThreeData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationToRenewal,
    tabs,
  ]);

  const values = useMemo(
    () => ({
      activeKey,
      addNewTab,
      allFeatures,
      duplicateTab,
      getFeaturesRisks,
      getListPlansActivity,
      hasClose,
      hasOpen,
      keyTabToDelete,
      newTabIndex,
      plansByActivityList,
      plansByActivityListCopy,
      removeTab,
      resetTabs,
      setActiveKey,
      setAllFeatures,
      setKeyTabToDelete,
      setPlansByActivityList,
      setShowModal,
      setTabs,
      showModal,
      stepThreeForm,
      tabs,
      setCommerceFormValues,
      commerceFormValues
    }),
    [
      activeKey,
      addNewTab,
      allFeatures,
      duplicateTab,
      getFeaturesRisks,
      getListPlansActivity,
      hasClose,
      hasOpen,
      keyTabToDelete,
      newTabIndex,
      plansByActivityList,
      plansByActivityListCopy,
      removeTab,
      resetTabs,
      setActiveKey,
      setAllFeatures,
      setKeyTabToDelete,
      setPlansByActivityList,
      setShowModal,
      setTabs,
      showModal,
      stepThreeForm,
      tabs,
      setCommerceFormValues,
      commerceFormValues
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
